import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  createSupplier,
  listSuppliers,
  updateSupplier,
  type Supplier,
  type SupplierInput,
} from '../lib/api';

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

/**
 * Suppliers directory (Commerce). Suppliers are who the company buys stock from;
 * products link to them via the sourcing list (Product editor → Suppliers tab),
 * and a purchase order targets one supplier.
 */
export function SuppliersPage() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['suppliers'], queryFn: () => listSuppliers() });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['suppliers'] });
  const create = useMutation({
    mutationFn: createSupplier,
    meta: { successMessage: 'Supplier created.' },
    onSuccess: () => {
      invalidate();
      setCreating(false);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<SupplierInput> }) =>
      updateSupplier(id, patch),
    meta: { successMessage: 'Supplier saved.' },
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });

  const suppliers = query.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Suppliers</h1>
        <Button onClick={() => setCreating(true)}>+ New supplier</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Vendors you buy stock from. Link products to a supplier in the product editor's Suppliers
        tab, then raise a purchase order against the supplier.
      </p>

      {creating && (
        <SupplierForm
          submitting={create.isPending}
          error={create.error}
          onSubmit={(input) => create.mutate(input)}
          onCancel={() => setCreating(false)}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>All suppliers</CardTitle>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : suppliers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No suppliers yet.</p>
          ) : (
            <ul className="divide-y">
              {suppliers.map((s) =>
                editing === s.id ? (
                  <li key={s.id} className="py-3">
                    <SupplierForm
                      supplier={s}
                      submitting={update.isPending}
                      error={update.error}
                      onSubmit={(input) => update.mutate({ id: s.id, patch: input })}
                      onCancel={() => setEditing(null)}
                    />
                  </li>
                ) : (
                  <li key={s.id} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">{s.name}</span>
                        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                          {s.code}
                        </span>
                        {!s.active && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                            inactive
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {[s.contactName, s.phone, s.email].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setEditing(s.id)}>
                      Edit
                    </Button>
                  </li>
                ),
              )}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SupplierForm({
  supplier,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  supplier?: Supplier;
  submitting: boolean;
  error: unknown;
  onSubmit: (input: SupplierInput) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(supplier?.code ?? '');
  const [name, setName] = useState(supplier?.name ?? '');
  const [contactName, setContactName] = useState(supplier?.contactName ?? '');
  const [email, setEmail] = useState(supplier?.email ?? '');
  const [phone, setPhone] = useState(supplier?.phone ?? '');
  const [paymentTerms, setPaymentTerms] = useState(supplier?.paymentTerms ?? '');
  const [active, setActive] = useState(supplier?.active ?? true);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          code: code.toUpperCase(),
          name: name.trim(),
          contactName: contactName.trim() || null,
          email: email.trim() || null,
          phone: phone.trim() || null,
          paymentTerms: paymentTerms.trim() || null,
          active,
        });
      }}
      className="grid grid-cols-1 gap-3 rounded-md border bg-muted/30 p-4 md:grid-cols-2"
    >
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Code</label>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          disabled={!!supplier}
          placeholder="ACME, SUP01"
          required
        />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Name</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Contact person</label>
        <Input value={contactName} onChange={(e) => setContactName(e.target.value)} />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Phone</label>
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Email</label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Payment terms</label>
        <Input
          value={paymentTerms}
          onChange={(e) => setPaymentTerms(e.target.value)}
          placeholder="Net 30, COD…"
        />
      </div>
      <label className="flex items-center gap-2 text-sm md:col-span-2">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        Active
      </label>
      {errText(error) && <p className="text-sm text-destructive md:col-span-2">{errText(error)}</p>}
      <div className="flex gap-2 md:col-span-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : supplier ? 'Save' : 'Create'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
